import React, { useEffect, useState } from 'react';
import { CompoundEntityRef, Entity } from '@backstage/catalog-model';
import {
  EntityRefLink,
  EntityRefPresentationSnapshot,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import { useCopyToClipboard } from 'react-use';
import { TinyChip } from '../TinyChip';

/** Where the CMDB provider puts the vuid of an entity, see `ANNOTATION_VUID`. */
const ANNOTATION_VUID = 'schema.dp.netrtl.com/vuid';

/** The resource types the CMDB gives a vuid, see `asClusterEntity`. */
const RESOURCE_TYPES_WITH_VUID = ['k8s-cluster', 'k8s-cluster-slice'];

/**
 * The vuid of an entity, or none for one the CMDB gives none.
 *
 * A department, a team, a product or a Kubernetes cluster or slice is talked
 * about by its vuid as often as by its name - a ticket, a chat message or a
 * CMDB query carries the vuid, the page carries the name - so the two go
 * together wherever one of them is named.
 *
 * The provider stamps the vuid onto the entity, and that is what is read where
 * the entity comes with its annotations. A link given nothing but a ref gets
 * the entity from the entity presentation, which leaves the annotations out;
 * those kinds are named by their vuid, so the name stands in.
 *
 * @public
 */
export function vuidOf(entity: Entity): string | undefined {
  const annotations = entity.metadata.annotations;
  if (annotations) {
    return annotations[ANNOTATION_VUID] || undefined;
  }
  const kind = entity.kind.toLocaleLowerCase('en-US');
  const type = entity.spec?.type as string | undefined;
  const hasVuid =
    kind === 'product' ||
    (kind === 'group' && (type === 'team' || type === 'department')) ||
    (kind === 'resource' &&
      type !== undefined &&
      RESOURCE_TYPES_WITH_VUID.includes(type));
  return hasVuid ? entity.metadata.name : undefined;
}

/**
 * What the entity presentation of the app hands over for an entity: the
 * stock snapshot, and the vuid of {@link vuidOf} where there is one - see
 * `renderEntityPresentation` in the app. It travels with the snapshot so that
 * a link given nothing but a ref learns it once the catalog answers, the way
 * it learns the title.
 *
 * @public
 */
export type EntityPresentationWithVuid = EntityRefPresentationSnapshot & {
  vuid?: string;
};

/** How long the tooltip says the vuid was copied. */
const COPIED_FOR_MS = 1500;

/**
 * The vuid of a team or a product as a tiny chip that reads VUID and copies
 * the vuid when clicked - it is what a ticket, a chat message or a CMDB query
 * asks for. The chip names what it holds rather than spelling it out, which
 * keeps it as short as the chips next to it; the vuid itself is on hover.
 *
 * @public
 */
export const VuidChip = (props: { vuid: string }) => {
  const { vuid } = props;
  const [, copy] = useCopyToClipboard();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return undefined;
    }
    const timer = setTimeout(() => setCopied(false), COPIED_FOR_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <TinyChip
      label="VUID"
      tooltip={copied ? `Copied ${vuid}` : `${vuid} - click to copy`}
      color="neutral"
      onClick={() => {
        copy(vuid);
        setCopied(true);
      }}
    />
  );
};

/**
 * A link to an entity, followed by its vuid chip where it has one. For a link
 * given nothing but a ref the vuid comes with the entity presentation, once
 * the catalog has answered.
 *
 * @public
 */
export const EntityRefLinkWithVuid = (props: {
  entityRef: Entity | CompoundEntityRef | string;
  defaultKind?: string;
}) => {
  const { entityRef, defaultKind } = props;
  const { vuid } = useEntityPresentation(entityRef, {
    defaultKind,
  }) as EntityPresentationWithVuid;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <EntityRefLink entityRef={entityRef} defaultKind={defaultKind} />
      {vuid && <VuidChip vuid={vuid} />}
    </span>
  );
};

/**
 * Several links to entities, each followed by its vuid chip, wrapping as the
 * room runs out - see {@link EntityRefLinkWithVuid}.
 *
 * @public
 */
export const EntityRefLinksWithVuid = (props: {
  entityRefs: (Entity | CompoundEntityRef | string)[];
  defaultKind?: string;
}) => (
  <span
    style={{
      display: 'inline-flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      columnGap: 12,
      rowGap: 4,
    }}
  >
    {props.entityRefs.map(ref => (
      <EntityRefLinkWithVuid
        key={typeof ref === 'string' ? ref : JSON.stringify(ref)}
        entityRef={ref}
        defaultKind={props.defaultKind}
      />
    ))}
  </span>
);
